import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { LogIn, LogOut, User, Wallet, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';

interface LoginButtonProps {
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function LoginButton({ variant = 'default', size = 'md', className }: LoginButtonProps) {
  const { isAuthenticated, isLoading, login, logout, user } = useAuth();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (isLoading) {
    return (
      <div className={cn(
        "flex items-center justify-center",
        size === 'sm' && "px-3 py-1.5",
        size === 'md' && "px-4 py-2",
        size === 'lg' && "px-6 py-3",
        className
      )}>
        <div className="w-4 h-4 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated && user) {
    return (
      <div className="relative" ref={dropdownRef}>
        <motion.button
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          onClick={() => setShowDropdown(!showDropdown)}
          className={cn(
            "flex items-center gap-2 bg-surface border border-line rounded-full hover:border-gold/30 transition-all",
            size === 'sm' && "px-2 py-1",
            size === 'md' && "px-3 py-1.5",
            size === 'lg' && "px-4 py-2",
          )}
        >
          <div className="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center">
            <User size={14} className="text-gold" />
          </div>
          <span className="text-xs font-mono text-ink hidden sm:block">
            {user.walletAddress
              ? `${user.walletAddress.slice(0, 6)}...${user.walletAddress.slice(-4)}`
              : user.email?.slice(0, 8) + '...'
            }
          </span>
          <ChevronDown size={14} className={cn("text-muted transition-transform", showDropdown && "rotate-180")} />
        </motion.button>

        {/* Dropdown Menu */}
        <AnimatePresence>
          {showDropdown && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-full mt-2 w-48 bg-surface border border-line rounded-xl shadow-xl overflow-hidden z-50"
            >
              <button
                onClick={() => {
                  navigate('/my-agent');
                  setShowDropdown(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-ink hover:bg-surface/80 transition-colors"
              >
                <User size={16} className="text-gold" />
                My Agent
              </button>
              <div className="border-t border-line/50" />
              <button
                onClick={() => {
                  logout();
                  setShowDropdown(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <LogOut size={16} />
                Disconnect
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={login}
      className={cn(
        "flex items-center gap-2 font-bold uppercase tracking-wider transition-all",
        variant === 'default' && "bg-gold text-bg hover:bg-white",
        variant === 'outline' && "border border-gold text-gold hover:bg-gold/10",
        variant === 'ghost' && "text-gold hover:bg-gold/10",
        size === 'sm' && "px-3 py-1.5 text-[10px] rounded",
        size === 'md' && "px-4 py-2 text-xs rounded-sm",
        size === 'lg' && "px-6 py-3 text-sm rounded-sm",
        className
      )}
    >
      <Wallet size={size === 'sm' ? 14 : size === 'lg' ? 20 : 16} />
      <span>Connect</span>
    </motion.button>
  );
}
